import { takeLatest, put, call, all, fork } from 'redux-saga/effects';
import VisitorPassTypes from './visitorPass.types';
import {
	createVisitorPassData,
	getAllVisitorPassesData,
	getVisitorPassData,
	updateVisitorPassData,
	deleteVisitorPassData,
} from './visitorPass.services';
import {
	createVisitorPassSuccess,
	createVisitorPassFailure,
	getAllVisitorPassesSuccess,
	getAllVisitorPassesFailure,
	getVisitorPassSuccess,
	getVisitorPassFailure,
	updateVisitorPassSuccess,
	updateVisitorPassFailure,
	deleteVisitorPassSuccess,
	deleteVisitorPassFailure,
} from './visitorPass.action';
import AlertManager from '../../utils/alertManager';
import { encryptData } from '../_common/enode-decode';

// Helper function to convert FormData to plain object
const formDataToObject = (formData: FormData): Record<string, any> => {
	const obj: Record<string, any> = {};
	formData.forEach((value, key) => {
		// FormData can have multiple values for the same key
		// If key already exists, convert to array
		if (obj[key]) {
			if (Array.isArray(obj[key])) {
				obj[key].push(value);
			} else {
				obj[key] = [obj[key], value];
			}
		} else {
			obj[key] = value;
		}
	});
	return obj;
};

export function* createVisitorPassDataSaga(payload: any) {
	try {
		if (process.env.NODE_ENV === 'development') console.log('createVisitorPassDataSaga - Received payload:', payload);

		// Extract data from FormData if it's FormData, otherwise use payload directly
		let dataToEncrypt: Record<string, any>;

		if (payload?.payload instanceof FormData) {
			if (process.env.NODE_ENV === 'development') console.log('Payload is FormData, extracting values...');
			dataToEncrypt = formDataToObject(payload.payload);
			if (process.env.NODE_ENV === 'development') console.log('Extracted data from FormData:', dataToEncrypt);
		} else {
			if (process.env.NODE_ENV === 'development') console.log('Payload is not FormData, using directly');
			dataToEncrypt = payload.payload || {};
		}

		const key = 'createVisitorPassKey';
		const encryptedData = encryptData(dataToEncrypt, key);
		if (process.env.NODE_ENV === 'development') console.log('Encrypted data length:', encryptedData.length);

		// @ts-ignore
		const response = yield call(createVisitorPassData, encryptedData);
		if (process.env.NODE_ENV === 'development') console.log('Create Visitor Pass response:', response);
		if (process.env.NODE_ENV === 'development') console.log('Create Visitor Pass response.data:', response.data);
		if (process.env.NODE_ENV === 'development') console.log('Create Visitor Pass response.data.status:', response.data?.status);
		if (process.env.NODE_ENV === 'development') console.log('Create Visitor Pass response.status:', response.status);
		if (process.env.NODE_ENV === 'development') console.log('Full response.data object:', JSON.stringify(response.data, null, 2));
		if (response.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			if (response?.data?.status === 'error') {
				if (process.env.NODE_ENV === 'development') console.log('Received failure response');
				yield put(createVisitorPassFailure(response.data));
			} else if (response?.data?.status === 'success') {
				if (process.env.NODE_ENV === 'development') console.log('Received success response');
				yield put(createVisitorPassSuccess(response.data));
			} else {
				if (process.env.NODE_ENV === 'development') console.log('Received unknown response, treating as success');
				if (process.env.NODE_ENV === 'development') console.log(
					'Response status check failed - response.data.status:',
					response.data?.status,
				);
				yield put(createVisitorPassSuccess(response.data));
			}
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('Error in createVisitorPass Data:', error);
		yield put(
			createVisitorPassFailure(
				error?.response?.data || { status: 'error', message: 'An error occurred' },
			),
		);
	}
}

export function* getAllVisitorPassesDataSaga(payload: any) {
	try {
		const key = 'getAllVisitorPasseskey';
		const encryptedData = encryptData(payload.payload, key);
		if (process.env.NODE_ENV === 'development') console.log('getAllVisitorPassesDataSaga: Starting with payload:', payload);
		// @ts-ignore
		const response = yield call(getAllVisitorPassesData, encryptedData);
		if (process.env.NODE_ENV === 'development') console.log('Get All Visitor Passes response:', response);
		if (process.env.NODE_ENV === 'development') console.log('Get All Visitor Passes response.data:', response.data);
		if (response.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			if (response?.data?.status === 'error') {
				if (process.env.NODE_ENV === 'development') console.log('Received failure response');
				yield put(getAllVisitorPassesFailure(response.data));
			} else if (response?.data?.status === 'success') {
				if (process.env.NODE_ENV === 'development') console.log('Received success response');
				yield put(getAllVisitorPassesSuccess(response.data));
			} else {
				if (process.env.NODE_ENV === 'development') console.log('Received unknown response, treating as success');
				yield put(getAllVisitorPassesSuccess(response.data));
			}
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('Error in getAllVisitorPassesData:', error);
		yield put(
			getAllVisitorPassesFailure(
				error?.response?.data || { status: 'error', message: 'An error occurred' },
			),
		);
	}
}

export function* getVisitorPassDataSaga(payload: any) {
	try {
		const key = 'getVisitorPasskey';
		const encryptedData = encryptData(payload.payload, key);
		// @ts-ignore
		const response = yield call(getVisitorPassData, encryptedData);
		if (process.env.NODE_ENV === 'development') console.log('Get Visitor Pass response:', response);
		if (response.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			if (response?.data?.status === 'error') {
				if (process.env.NODE_ENV === 'development') console.log('Received failure response');
				yield put(getVisitorPassFailure(response.data));
			} else if (response?.data?.status === 'success') {
				if (process.env.NODE_ENV === 'development') console.log('Received success response');
				yield put(getVisitorPassSuccess(response.data));
			} else {
				if (process.env.NODE_ENV === 'development') console.log('Received unknown response, treating as success');
				yield put(getVisitorPassSuccess(response.data));
			}
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('Error in getVisitorPassData:', error);
		yield put(
			getVisitorPassFailure(
				error?.response?.data || { status: 'error', message: 'An error occurred' },
			),
		);
	}
}

export function* updateVisitorPassDataSaga(payload: any) {
	try {
		if (process.env.NODE_ENV === 'development') console.log('updateVisitorPassDataSaga - Received payload:', payload);

		// Extract data from FormData if it's FormData, otherwise use payload directly
		let dataToEncrypt: Record<string, any>;

		if (payload?.payload instanceof FormData) {
			if (process.env.NODE_ENV === 'development') console.log('Payload is FormData, extracting values...');
			dataToEncrypt = formDataToObject(payload.payload);
			if (process.env.NODE_ENV === 'development') console.log('Extracted data from FormData:', dataToEncrypt);
		} else {
			if (process.env.NODE_ENV === 'development') console.log('Payload is not FormData, using directly');
			dataToEncrypt = payload.payload || {};
		}

		const key = 'updateVisitorPassKey';
		const encryptedData = encryptData(dataToEncrypt, key);
		if (process.env.NODE_ENV === 'development') console.log('Encrypted data length:', encryptedData.length);

		// @ts-ignore
		const response = yield call(updateVisitorPassData, encryptedData);
		if (process.env.NODE_ENV === 'development') console.log('Update Visitor Pass response:', response);
		if (response.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			if (response?.data?.status === 'error') {
				if (process.env.NODE_ENV === 'development') console.log('Received failure response');
				yield put(updateVisitorPassFailure(response.data));
			} else if (response?.data?.status === 'success') {
				if (process.env.NODE_ENV === 'development') console.log('Received success response');
				yield put(updateVisitorPassSuccess(response.data));
			} else {
				if (process.env.NODE_ENV === 'development') console.log('Received unknown response, treating as success');
				yield put(updateVisitorPassSuccess(response.data));
			}
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('Error in updateVisitorPassData:', error);
		yield put(
			updateVisitorPassFailure(
				error?.response?.data || { status: 'error', message: 'An error occurred' },
			),
		);
	}
}

export function* deleteVisitorPassDataSaga(payload: any) {
	try {
		const key = 'deleteVisitorPassKey';
		const encryptedData = encryptData(payload.payload, key);
		// @ts-ignore
		const response = yield call(deleteVisitorPassData, encryptedData);
		if (process.env.NODE_ENV === 'development') console.log('Delete Visitor Pass response:', response);
		if (response.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			if (response?.data?.status === 'error') {
				if (process.env.NODE_ENV === 'development') console.log('Received failure response');
				yield put(deleteVisitorPassFailure(response.data));
			} else if (response?.data?.status === 'success') {
				if (process.env.NODE_ENV === 'development') console.log('Received success response');
				yield put(deleteVisitorPassSuccess(response.data));
			} else {
				if (process.env.NODE_ENV === 'development') console.log('Received unknown response, treating as success');
				yield put(deleteVisitorPassSuccess(response.data));
			}
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('Error in deleteVisitorPassData:', error);
		yield put(
			deleteVisitorPassFailure(
				error?.response?.data || { status: 'error', message: 'An error occurred' },
			),
		);
	}
}

export function* onCreateVisitorPassStart(): Generator<any, void, any> {
	yield takeLatest(VisitorPassTypes.CREATE_VISITOR_PASS_START as any, createVisitorPassDataSaga);
}

export function* onGetAllVisitorPassesStart(): Generator<any, void, any> {
	if (process.env.NODE_ENV === 'development') console.log('onGetAllVisitorPassesStart: Setting up listener for GET_ALL_VISITOR_PASSES_START');
	yield takeLatest(
		VisitorPassTypes.GET_ALL_VISITOR_PASSES_START as any,
		getAllVisitorPassesDataSaga,
	);
}

export function* onGetVisitorPassStart(): Generator<any, void, any> {
	yield takeLatest(VisitorPassTypes.GET_VISITOR_PASS_START as any, getVisitorPassDataSaga);
}

export function* onUpdateVisitorPassStart(): Generator<any, void, any> {
	yield takeLatest(VisitorPassTypes.UPDATE_VISITOR_PASS_START as any, updateVisitorPassDataSaga);
}

export function* onDeleteVisitorPassStart(): Generator<any, void, any> {
	yield takeLatest(VisitorPassTypes.DELETE_VISITOR_PASS_START as any, deleteVisitorPassDataSaga);
}

export function* visitorPassDetailsSaga(): Generator<any, void, any> {
	if (process.env.NODE_ENV === 'development') console.log('visitorPassDetailsSaga: Starting saga');
	yield all([
		fork(onCreateVisitorPassStart),
		fork(onGetAllVisitorPassesStart),
		fork(onGetVisitorPassStart),
		fork(onUpdateVisitorPassStart),
		fork(onDeleteVisitorPassStart),
	]);
	if (process.env.NODE_ENV === 'development') console.log('visitorPassDetailsSaga: All sagas forked');
}
